// src/redux/campaignsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createCampaign as createCampaignService,
  listCampaigns as listCampaignsService,
  getCampaign as getCampaignService,
  patchCampaign as patchCampaignService,
  deleteCampaign as deleteCampaignService,
  addInfluencersToCampaign as addInfluencersToCampaignService,
  removeInfluencerFromCampaign as removeInfluencerFromCampaignService,
  removeInfluencersFromCampaign as removeInfluencersFromCampaignService,
} from "../services/campaignsService";

const initialState = {
  items: [],

  // NOTE: We keep the field name "lastKey" for backwards compatibility in UI,
  // but it now represents a generic pagination cursor (next_cursor/nextCursor/cursor/last_key/lastKey).
  lastKey: null,
  hasMore: true,

  loading: false,
  error: null,

  creating: false,
  createError: null,

  fetchingOne: false,
  fetchOneError: null,
  current: null,

  updating: false,
  updateError: null,

  deleting: false,
  deleteError: null,

  addingInfluencers: false,
  addInfluencersError: null,

  // ✅ new: remove influencers state (single + batch)
  removingInfluencers: false,
  removeInfluencersError: null,
};

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

const normalizeId = (id) => {
  if (id == null) return null;
  const s = String(id).trim();
  return s ? s : null;
};

const getCampaignId = (c) => {
  const id = c?.campaignId ?? c?.id ?? c?._id ?? c?.campaign_id ?? null;
  return normalizeId(id);
};

const upsertOne = (items, campaign) => {
  const id = getCampaignId(campaign);
  if (!id) return items || [];

  const arr = items || [];
  const idx = arr.findIndex((x) => getCampaignId(x) === id);
  if (idx === -1) return [campaign, ...arr];

  const next = [...arr];
  next[idx] = campaign;
  return next;
};

const upsertMany = (existing, incoming) => {
  const map = new Map();

  for (const c of existing || []) {
    const id = getCampaignId(c);
    if (id) map.set(id, c);
  }
  for (const c of incoming || []) {
    const id = getCampaignId(c);
    if (id) map.set(id, c);
  }
  return Array.from(map.values());
};

/**
 * Cursor normalization:
 * Backend may return any of:
 *  - next_cursor / nextCursor (preferred)
 *  - cursor
 *  - last_key / lastKey (legacy)
 */
const parseListResponse = (resp) => {
  if (Array.isArray(resp)) return { items: resp, cursor: null };

  const items = Array.isArray(resp?.items) ? resp.items : [];

  const rawCursor =
    resp?.next_cursor ??
    resp?.nextCursor ??
    resp?.cursor ??
    resp?.last_key ??
    resp?.lastKey ??
    null;

  const cursor = rawCursor == null ? null : String(rawCursor).trim();

  // treat "", "null", "nul" as no cursor
  if (!cursor || cursor.toLowerCase() === "null" || cursor.toLowerCase() === "nul") {
    return { items, cursor: null };
  }

  return { items, cursor };
};

const mergeCampaignPatch = (existing, incoming) => {
  if (!existing) return incoming || existing;
  if (!incoming || typeof incoming !== "object") return existing;

  // Merge so we don't accidentally drop fields not returned by some endpoints.
  const next = { ...existing, ...incoming };

  // Defensive: keep arrays only if they are arrays (avoid null nukes)
  if ("influencers" in incoming && !Array.isArray(incoming.influencers)) {
    delete next.influencers;
    next.influencers = existing.influencers;
  }
  if ("influencerRefs" in incoming && incoming.influencerRefs != null && !Array.isArray(incoming.influencerRefs)) {
    delete next.influencerRefs;
    next.influencerRefs = existing.influencerRefs;
  }

  return next;
};

// -------------------- THUNKS --------------------

export const listCampaignsAsync = createAsyncThunk(
  "campaigns/list",
  async ({ params = {}, append = false } = {}, { rejectWithValue }) => {
    try {
      const resp = await listCampaignsService(params);
      const { items, cursor } = parseListResponse(resp);
      return { items, lastKey: cursor, append };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to load campaigns."));
    }
  }
);

export const createCampaignAsync = createAsyncThunk(
  "campaigns/create",
  async (payload, { rejectWithValue }) => {
    try {
      if (!payload || typeof payload !== "object") {
        throw new Error("Valid payload is required to create a campaign.");
      }
      const created = await createCampaignService(payload);
      return created || null;
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to create campaign."));
    }
  }
);

/**
 * getCampaignAsync({ campaignId })  ✅ preferred
 * getCampaignAsync("123")          ✅ backward compatible
 */
export const getCampaignAsync = createAsyncThunk(
  "campaigns/getOne",
  async (arg, { rejectWithValue }) => {
    try {
      const campaignId = typeof arg === "object" && arg !== null ? arg.campaignId : arg;

      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");

      const campaign = await getCampaignService(id);
      return campaign || null;
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to load campaign."));
    }
  }
);

export const patchCampaignAsync = createAsyncThunk(
  "campaigns/patch",
  async ({ campaignId, payload }, { rejectWithValue }) => {
    try {
      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");
      if (!payload || typeof payload !== "object") {
        throw new Error("Valid payload is required to update a campaign.");
      }
      const updated = await patchCampaignService(id, payload);
      return updated || null;
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to update campaign."));
    }
  }
);

// ✅ nicer name for edit page usage
export const updateCampaignAsync = patchCampaignAsync;

/**
 * deleteCampaignAsync({ campaignId }) ✅ preferred
 * deleteCampaignAsync("123")         ✅ backward compatible
 */
export const deleteCampaignAsync = createAsyncThunk(
  "campaigns/delete",
  async (arg, { rejectWithValue }) => {
    try {
      const campaignId = typeof arg === "object" && arg !== null ? arg.campaignId : arg;

      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");

      const resp = await deleteCampaignService(id);
      return { campaignId: id, resp: resp || { ok: true } };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to delete campaign."));
    }
  }
);

/**
 * POST /campaigns/{campaignId}/influencers
 * Updates Redux store immediately with returned snapshot (influencers, influencerRefs, influencerCount, updatedAt).
 */
export const addInfluencersToCampaignAsync = createAsyncThunk(
  "campaigns/addInfluencers",
  async ({ campaignId, items }, { rejectWithValue }) => {
    try {
      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");
      if (!Array.isArray(items) || items.length === 0) throw new Error("items[] is required.");

      const result = await addInfluencersToCampaignService(id, items);
      return result || null;
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to add influencers to campaign."));
    }
  }
);

/**
 * DELETE /campaigns/{campaignId}/influencers/{handle}
 * Single remove.
 *
 * Returns: { campaignId, handle, result }
 * (result may include rebuilt snapshot if your backend returns it)
 */
export const removeInfluencerFromCampaignAsync = createAsyncThunk(
  "campaigns/removeInfluencer",
  async ({ campaignId, handle }, { rejectWithValue }) => {
    try {
      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");

      const h = String(handle ?? "").trim().replace(/^@/, "");
      if (!h) throw new Error("handle is required.");

      const result = await removeInfluencerFromCampaignService(id, h);
      return { campaignId: id, handle: h, result: result || null };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to remove influencer from campaign."));
    }
  }
);

/**
 * POST /campaigns/{campaignId}/influencers/remove
 * Batch remove.
 *
 * Returns: { campaignId, handles, result }
 */
export const removeInfluencersFromCampaignAsync = createAsyncThunk(
  "campaigns/removeInfluencers",
  async ({ campaignId, handles }, { rejectWithValue }) => {
    try {
      const id = normalizeId(campaignId);
      if (!id) throw new Error("campaignId is required.");

      if (!Array.isArray(handles) || handles.length === 0) {
        throw new Error("handles[] is required.");
      }

      const cleaned = handles
        .map((x) => String(x ?? "").trim().replace(/^@/, ""))
        .filter(Boolean);

      if (cleaned.length === 0) throw new Error("handles[] is required.");

      const result = await removeInfluencersFromCampaignService(id, cleaned);
      return { campaignId: id, handles: cleaned, result: result || null };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e, "Failed to remove influencers from campaign."));
    }
  }
);

// -------------------- SLICE --------------------

const campaignsSlice = createSlice({
  name: "campaigns",
  initialState,
  reducers: {
    clearCampaignsErrors(state) {
      state.error = null;
      state.createError = null;
      state.fetchOneError = null;
      state.updateError = null;
      state.deleteError = null;
      state.addInfluencersError = null;
      state.removeInfluencersError = null;
    },
    resetCampaignsState() {
      return { ...initialState };
    },
    clearCurrentCampaign(state) {
      state.current = null;
      state.fetchingOne = false;
      state.fetchOneError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(listCampaignsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listCampaignsAsync.fulfilled, (state, action) => {
        state.loading = false;

        const { items, lastKey, append } = action.payload || {
          items: [],
          lastKey: null,
          append: false,
        };

        state.items = append ? upsertMany(state.items, items || []) : upsertMany([], items || []);

        state.lastKey = lastKey || null;
        state.hasMore = Boolean(lastKey);
      })
      .addCase(listCampaignsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load campaigns.";
      })

      // CREATE
      .addCase(createCampaignAsync.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createCampaignAsync.fulfilled, (state, action) => {
        state.creating = false;

        const created = action.payload;
        if (!created) return;

        state.items = upsertOne(state.items, created);
        state.current = created;
      })
      .addCase(createCampaignAsync.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload || "Failed to create campaign.";
      })

      // GET ONE
      .addCase(getCampaignAsync.pending, (state) => {
        state.fetchingOne = true;
        state.fetchOneError = null;
      })
      .addCase(getCampaignAsync.fulfilled, (state, action) => {
        state.fetchingOne = false;

        const campaign = action.payload;
        state.current = campaign || null;

        if (campaign) state.items = upsertOne(state.items, campaign);
      })
      .addCase(getCampaignAsync.rejected, (state, action) => {
        state.fetchingOne = false;
        state.fetchOneError = action.payload || "Failed to load campaign.";
      })

      // PATCH / UPDATE
      .addCase(patchCampaignAsync.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(patchCampaignAsync.fulfilled, (state, action) => {
        state.updating = false;

        const updated = action.payload;
        if (!updated) return;

        const id = getCampaignId(updated);

        // merge into existing if present (prevents dropping fields)
        const existing = state.items.find((c) => getCampaignId(c) === id);
        const merged = mergeCampaignPatch(existing, updated);

        state.items = upsertOne(state.items, merged);

        if (id && getCampaignId(state.current) === id) {
          state.current = mergeCampaignPatch(state.current, updated);
        }
      })
      .addCase(patchCampaignAsync.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || "Failed to update campaign.";
      })

      // DELETE
      .addCase(deleteCampaignAsync.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteCampaignAsync.fulfilled, (state, action) => {
        state.deleting = false;

        const id = normalizeId(action.payload?.campaignId);
        if (!id) return;

        state.items = (state.items || []).filter((c) => getCampaignId(c) !== id);
        if (getCampaignId(state.current) === id) state.current = null;
      })
      .addCase(deleteCampaignAsync.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || "Failed to delete campaign.";
      })

      // ADD INFLUENCERS (update store immediately)
      .addCase(addInfluencersToCampaignAsync.pending, (state) => {
        state.addingInfluencers = true;
        state.addInfluencersError = null;
      })
      .addCase(addInfluencersToCampaignAsync.fulfilled, (state, action) => {
        state.addingInfluencers = false;

        const result = action.payload;
        if (!result || typeof result !== "object") return;

        const cid = normalizeId(result.campaignId || result.campaign_id);
        if (!cid) return;

        const mergeSnapshot = (c) => {
          if (!c) return c;
          const id = getCampaignId(c);
          if (id !== cid) return c;

          const next = { ...c };
          if (result.influencers != null) next.influencers = result.influencers;
          if (result.influencerRefs != null) next.influencerRefs = result.influencerRefs;
          if (result.influencerCount != null) next.influencerCount = result.influencerCount;
          if (result.updatedAt != null) next.updatedAt = result.updatedAt;
          return next;
        };

        state.items = (state.items || []).map(mergeSnapshot);
        if (getCampaignId(state.current) === cid) state.current = mergeSnapshot(state.current);
      })
      .addCase(addInfluencersToCampaignAsync.rejected, (state, action) => {
        state.addingInfluencers = false;
        state.addInfluencersError = action.payload || "Failed to add influencers to campaign.";
      })

      // REMOVE INFLUENCER(S)
      .addCase(removeInfluencerFromCampaignAsync.pending, (state) => {
        state.removingInfluencers = true;
        state.removeInfluencersError = null;
      })
      .addCase(removeInfluencerFromCampaignAsync.fulfilled, (state, action) => {
        state.removingInfluencers = false;

        const campaignId = normalizeId(action.payload?.campaignId);
        if (!campaignId) return;

        const result = action.payload?.result;

        // If backend returns rebuilt snapshot, trust it.
        const hasSnapshot =
          result &&
          typeof result === "object" &&
          ("influencers" in result || "influencerRefs" in result || "influencerCount" in result);

        if (hasSnapshot) {
          const apply = (c) => {
            if (!c) return c;
            if (getCampaignId(c) !== campaignId) return c;

            const next = { ...c };
            if (result.influencers != null) next.influencers = result.influencers;
            if (result.influencerRefs != null) next.influencerRefs = result.influencerRefs;
            if (result.influencerCount != null) next.influencerCount = result.influencerCount;
            if (result.updatedAt != null) next.updatedAt = result.updatedAt;
            return next;
          };

          state.items = (state.items || []).map(apply);
          if (getCampaignId(state.current) === campaignId) state.current = apply(state.current);
          return;
        }

        // Otherwise do an optimistic local removal based on handle
        const handle = String(action.payload?.handle ?? "").trim().replace(/^@/, "");
        if (!handle) return;

        const applyOptimistic = (c) => {
          if (!c) return c;
          if (getCampaignId(c) !== campaignId) return c;

          const next = { ...c };

          if (Array.isArray(next.influencers)) {
            next.influencers = next.influencers.filter(
              (h) => String(h ?? "").trim().replace(/^@/, "").toLowerCase() !== handle.toLowerCase()
            );
          }

          if (Array.isArray(next.influencerRefs)) {
            next.influencerRefs = next.influencerRefs.filter((r) => {
              const u = String(r?.username ?? r?.handle ?? "").trim().replace(/^@/, "");
              return u.toLowerCase() !== handle.toLowerCase();
            });
          }

          if (typeof next.influencerCount === "number") {
            next.influencerCount = Array.isArray(next.influencers) ? next.influencers.length : next.influencerCount;
          }

          return next;
        };

        state.items = (state.items || []).map(applyOptimistic);
        if (getCampaignId(state.current) === campaignId) state.current = applyOptimistic(state.current);
      })
      .addCase(removeInfluencerFromCampaignAsync.rejected, (state, action) => {
        state.removingInfluencers = false;
        state.removeInfluencersError = action.payload || "Failed to remove influencer from campaign.";
      })

      .addCase(removeInfluencersFromCampaignAsync.pending, (state) => {
        state.removingInfluencers = true;
        state.removeInfluencersError = null;
      })
      .addCase(removeInfluencersFromCampaignAsync.fulfilled, (state, action) => {
        state.removingInfluencers = false;

        const campaignId = normalizeId(action.payload?.campaignId);
        if (!campaignId) return;

        const result = action.payload?.result;

        const hasSnapshot =
          result &&
          typeof result === "object" &&
          ("influencers" in result || "influencerRefs" in result || "influencerCount" in result);

        if (hasSnapshot) {
          const apply = (c) => {
            if (!c) return c;
            if (getCampaignId(c) !== campaignId) return c;

            const next = { ...c };
            if (result.influencers != null) next.influencers = result.influencers;
            if (result.influencerRefs != null) next.influencerRefs = result.influencerRefs;
            if (result.influencerCount != null) next.influencerCount = result.influencerCount;
            if (result.updatedAt != null) next.updatedAt = result.updatedAt;
            return next;
          };

          state.items = (state.items || []).map(apply);
          if (getCampaignId(state.current) === campaignId) state.current = apply(state.current);
          return;
        }

        // optimistic fallback using handles list
        const handles = Array.isArray(action.payload?.handles) ? action.payload.handles : [];
        const set = new Set(
          handles.map((h) => String(h ?? "").trim().replace(/^@/, "").toLowerCase()).filter(Boolean)
        );
        if (set.size === 0) return;

        const applyOptimistic = (c) => {
          if (!c) return c;
          if (getCampaignId(c) !== campaignId) return c;

          const next = { ...c };

          if (Array.isArray(next.influencers)) {
            next.influencers = next.influencers.filter((h) => {
              const k = String(h ?? "").trim().replace(/^@/, "").toLowerCase();
              return !set.has(k);
            });
          }

          if (Array.isArray(next.influencerRefs)) {
            next.influencerRefs = next.influencerRefs.filter((r) => {
              const u = String(r?.username ?? r?.handle ?? "").trim().replace(/^@/, "").toLowerCase();
              return !set.has(u);
            });
          }

          if (typeof next.influencerCount === "number") {
            next.influencerCount = Array.isArray(next.influencers) ? next.influencers.length : next.influencerCount;
          }

          return next;
        };

        state.items = (state.items || []).map(applyOptimistic);
        if (getCampaignId(state.current) === campaignId) state.current = applyOptimistic(state.current);
      })
      .addCase(removeInfluencersFromCampaignAsync.rejected, (state, action) => {
        state.removingInfluencers = false;
        state.removeInfluencersError = action.payload || "Failed to remove influencers from campaign.";
      });
  },
});

export const { clearCampaignsErrors, resetCampaignsState, clearCurrentCampaign } =
  campaignsSlice.actions;

// -------------------- SELECTORS --------------------

export const selectCampaigns = (state) => state.campaigns?.items || [];
export const selectCampaignsLoading = (state) => state.campaigns?.loading || false;
export const selectCampaignsError = (state) => state.campaigns?.error || null;

export const selectCampaignsLastKey = (state) => state.campaigns?.lastKey || null;
export const selectCampaignsHasMore = (state) => state.campaigns?.hasMore || false;

export const selectCampaignCreating = (state) => state.campaigns?.creating || false;
export const selectCampaignCreateError = (state) => state.campaigns?.createError || null;

export const selectCurrentCampaign = (state) => state.campaigns?.current || null;
export const selectCurrentCampaignLoading = (state) => state.campaigns?.fetchingOne || false;
export const selectCurrentCampaignError = (state) => state.campaigns?.fetchOneError || null;

export const selectCampaignUpdating = (state) => state.campaigns?.updating || false;
export const selectCampaignUpdateError = (state) => state.campaigns?.updateError || null;

export const selectCampaignDeleting = (state) => state.campaigns?.deleting || false;
export const selectCampaignDeleteError = (state) => state.campaigns?.deleteError || null;

export const selectAddingInfluencers = (state) => state.campaigns?.addingInfluencers || false;
export const selectAddInfluencersError = (state) => state.campaigns?.addInfluencersError || null;

export const selectRemovingInfluencers = (state) => state.campaigns?.removingInfluencers || false;
export const selectRemoveInfluencersError = (state) => state.campaigns?.removeInfluencersError || null;

// ✅ ALIASES (so your CampaignEditPage imports work)
export const selectCampaignLoadingOne = selectCurrentCampaignLoading;
export const selectCampaignOneError = selectCurrentCampaignError;

// ✅ This is what your Details/Edit page needs
export const selectCampaignById = (state, campaignId) => {
  const id = normalizeId(campaignId);
  if (!id) return null;

  const current = state.campaigns?.current || null;
  if (current && getCampaignId(current) === id) return current;

  const items = state.campaigns?.items || [];
  return items.find((c) => getCampaignId(c) === id) || null;
};

export default campaignsSlice.reducer;
